export type UasGrade = "A" | "B" | "C" | "D" | "E" | "S" | "U";

export type UasCourseTargetSource = {
  id: string;
  label: string;
  url: string;
  notifiedDate?: string;
  assumedGpGrade?: UasGrade;
};

export type H2Subject =
  | "Mathematics"
  | "Physics"
  | "Chemistry"
  | "Biology"
  | "Computing"
  | "Further Mathematics"
  | "Economics";

export type SubjectPrerequisite = {
  subject: H2Subject;
  type: "required" | "recommended";
};

export type UasCourseTarget = {
  id: string;
  label: string;
  university: "NUS" | "SMU" | "NTU";
  programme: string;
  gradeProfile10th: string;
  gradeProfile90th: string;
  baseUas10th: number;
  baseUas90th: number;
  source: UasCourseTargetSource;
  prerequisites: readonly SubjectPrerequisite[];
};

type Grade = UasGrade;

const H2_POINTS: Record<Grade, number> = {
  A: 20,
  B: 17.5,
  C: 15,
  D: 12.5,
  E: 10,
  S: 5,
  U: 0,
};

const H1_POINTS: Record<Grade, number> = {
  A: 10,
  B: 8.75,
  C: 7.5,
  D: 6.25,
  E: 5,
  S: 2.5,
  U: 0,
};

function gradeProfileToBaseUas(
  profile: string,
  assumedGpGrade: Grade
): number | null {
  const match = profile.trim().match(/^([A-Z]{3})\/([A-Z])$/);
  if (!match) return null;

  const h2 = match[1]?.split("") as Grade[] | undefined;
  if (!h2?.length) return null;

  const h2Points = h2.map((grade) => H2_POINTS[grade]);
  const gpPoints = H1_POINTS[assumedGpGrade];
  if (h2Points.some((value) => value == null) || gpPoints == null) return null;

  return h2Points.reduce((sum, value) => sum + value, 0) + gpPoints;
}

const nusIgpSource: UasCourseTargetSource = {
  id: "nus-oam-igp-ay2025-2026",
  label:
    "NUS Office of Admissions - Indicative Grade Profile (AY2025/2026 intake)",
  url: "https://nus.edu.sg/oam/admissions/indicative-grade-profile",
  assumedGpGrade: "C",
};

type NusRow = {
  programme: string;
  gradeProfile10th: string;
  gradeProfile90th: string;
  prerequisites?: readonly SubjectPrerequisite[];
};

const nusRows: readonly NusRow[] = [
  {
    programme: "Law",
    gradeProfile10th: "AAA/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Medicine",
    gradeProfile10th: "AAA/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Chemistry",
        type: "required",
      },
      {
        subject: "Biology",
        type: "recommended",
      },
      {
        subject: "Physics",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Nursing",
    gradeProfile10th: "CCD/C",
    gradeProfile90th: "ABB/B",
    prerequisites: [],
  },
  {
    programme: "Dentistry",
    gradeProfile10th: "AAA/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Chemistry",
        type: "required",
      },
      {
        subject: "Biology",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Architecture",
    gradeProfile10th: "CCC/C",
    gradeProfile90th: "AAB/B",
    prerequisites: [],
  },
  {
    programme: "Engineering",
    gradeProfile10th: "BBB/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Physics",
        type: "required",
      },
    ],
  },
  {
    programme: "Industrial Design",
    gradeProfile10th: "BBC/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Landscape Architecture",
    gradeProfile10th: "CCC/B",
    gradeProfile90th: "AAB/C",
    prerequisites: [],
  },
  {
    programme: "Business Analytics",
    gradeProfile10th: "AAA/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Business Artificial Intelligence Systems",
    gradeProfile10th: "AAA/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Common Computer Science Programmes",
    gradeProfile10th: "AAA/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Information Security",
    gradeProfile10th: "AAA/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Computer Engineering",
    gradeProfile10th: "AAA/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Physics",
        type: "required",
      },
    ],
  },
  {
    programme: "Data Science and Economics",
    gradeProfile10th: "AAA/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Environmental Studies",
    gradeProfile10th: "AAA/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Food Science and Technology",
    gradeProfile10th: "AAA/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Chemistry",
        type: "required",
      },
    ],
  },
  {
    programme: "Humanities and Sciences",
    gradeProfile10th: "ABB/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Pharmaceutical Science",
    gradeProfile10th: "AAA/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Chemistry",
        type: "required",
      },
    ],
  },
  {
    programme: "Pharmacy",
    gradeProfile10th: "AAA/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Chemistry",
        type: "required",
      },
    ],
  },
  {
    programme: "Philosophy, Politics, and Economics",
    gradeProfile10th: "AAA/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Business Administration",
    gradeProfile10th: "AAA/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "recommended",
      },
    ],
  },
] as const;

const smuIgpSource: UasCourseTargetSource = {
  id: "smu-oafa-igp-uae2025",
  label:
    "SMU Office of Admissions & Financial Assistance - Indicative Grade Profile (2025 offers, 2026 guidance)",
  url: "https://admissions.smu.edu.sg/admissions-requirements/indicative-grade-profile",
  notifiedDate: "2026-01-09",
  assumedGpGrade: "C",
};

type SmuRow = {
  programme: string;
  gradeProfile10th: string;
  gradeProfile90th: string;
  prerequisites?: readonly SubjectPrerequisite[];
};

const smuRows: readonly SmuRow[] = [
  {
    programme: "Accountancy",
    gradeProfile10th: "BBB/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Business Management",
    gradeProfile10th: "ABB/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Law",
    gradeProfile10th: "AAA/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Economics",
    gradeProfile10th: "BBB/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Information Systems",
    gradeProfile10th: "BBC/C",
    gradeProfile90th: "AAA/B",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Computer Science",
    gradeProfile10th: "AAB/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Computing & Law",
    gradeProfile10th: "ABB/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Software Engineering",
    gradeProfile10th: "BBC/B",
    gradeProfile90th: "AAA/C",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Social Sciences",
    gradeProfile10th: "BBB/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Deferred Declaration / Integrative Studies",
    gradeProfile10th: "ABB/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
] as const;

const ntuIgpSource: UasCourseTargetSource = {
  id: "ntu-ug-igp-ay2025-2026",
  label:
    "NTU Undergraduate Admissions - Indicative Grade Profile (AY2025/2026)",
  url: "https://www.ntu.edu.sg/admissions/undergraduate/important-links/indicative-grade-profile",
  assumedGpGrade: "C",
};

type NtuRow = {
  programme: string;
  gradeProfile10th: string;
  gradeProfile90th: string;
  prerequisites?: readonly SubjectPrerequisite[];
};

const ntuRows: readonly NtuRow[] = [
  {
    programme: "Medicine",
    gradeProfile10th: "AAA/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Chemistry",
        type: "required",
      },
      {
        subject: "Biology",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Renaissance Engineering",
    gradeProfile10th: "AAA/A",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Physics",
        type: "required",
      },
    ],
  },
  {
    programme: "Aerospace Engineering",
    gradeProfile10th: "ABC/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Physics",
        type: "required",
      },
    ],
  },
  {
    programme: "Bioengineering",
    gradeProfile10th: "BCC/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Physics",
        type: "recommended",
      },
      {
        subject: "Chemistry",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Chemical & Biomolecular Engineering",
    gradeProfile10th: "BCC/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Chemistry",
        type: "required",
      },
    ],
  },
  {
    programme: "Civil Engineering",
    gradeProfile10th: "CCD/D",
    gradeProfile90th: "AAB/B",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Physics",
        type: "required",
      },
    ],
  },
  {
    programme: "Electrical & Electronic Engineering",
    gradeProfile10th: "CCC/D",
    gradeProfile90th: "AAA/C",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Physics",
        type: "required",
      },
    ],
  },
  {
    programme: "Environmental Engineering",
    gradeProfile10th: "BCC/D",
    gradeProfile90th: "AAA/B",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Physics",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Information Engineering & Media",
    gradeProfile10th: "BCC/D",
    gradeProfile90th: "ABC/B",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Physics",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Maritime Studies",
    gradeProfile10th: "BCC/C",
    gradeProfile90th: "ABC/B",
    prerequisites: [],
  },
  {
    programme: "Materials Engineering",
    gradeProfile10th: "BCC/D",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Chemistry",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Mechanical Engineering",
    gradeProfile10th: "CCD/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Physics",
        type: "required",
      },
    ],
  },
  {
    programme: "Process Engineering and Synthetic Chemistry",
    gradeProfile10th: "ABC/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Robotics",
    gradeProfile10th: "BBC/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Artificial Intelligence and Society",
    gradeProfile10th: "ABC/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Computer Engineering",
    gradeProfile10th: "AAC/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Physics",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Computer Science",
    gradeProfile10th: "AAB/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Data Science & Artificial Intelligence",
    gradeProfile10th: "AAB/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Double Major Programmes",
    gradeProfile10th: "AAB/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Biological Sciences",
    gradeProfile10th: "AAB/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Chemistry",
        type: "required",
      },
      {
        subject: "Biology",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Chemistry & Biological Chemistry",
    gradeProfile10th: "BCC/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Chemistry",
        type: "required",
      },
    ],
  },
  {
    programme: "Chinese Medicine",
    gradeProfile10th: "ABC/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Chemistry",
        type: "required",
      },
    ],
  },
  {
    programme: "Environmental Earth Systems Science",
    gradeProfile10th: "AAB/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Chemistry",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Mathematical Sciences",
    gradeProfile10th: "BCC/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Physics / Applied Physics",
    gradeProfile10th: "CCC/D",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
      {
        subject: "Physics",
        type: "required",
      },
    ],
  },
  {
    programme: "Accountancy",
    gradeProfile10th: "BBC/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Business",
    gradeProfile10th: "BBC/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Applied Computing in Finance",
    gradeProfile10th: "AAC/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "SOH Double Major Programmes",
    gradeProfile10th: "AAA/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "SSS Double Major Programmes",
    gradeProfile10th: "AAA/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Art, Design & Media",
    gradeProfile10th: "CCC/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Chinese",
    gradeProfile10th: "BBC/C",
    gradeProfile90th: "AAA/C",
    prerequisites: [],
  },
  {
    programme: "Communication Studies",
    gradeProfile10th: "ABC/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Economics",
    gradeProfile10th: "ABC/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "Economics and Data Science",
    gradeProfile10th: "AAB/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "required",
      },
    ],
  },
  {
    programme: "English",
    gradeProfile10th: "BBC/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "History",
    gradeProfile10th: "BCC/B",
    gradeProfile90th: "AAB/C",
    prerequisites: [],
  },
  {
    programme: "Linguistics & Multilingual Studies",
    gradeProfile10th: "BBC/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Philosophy",
    gradeProfile10th: "BBC/C",
    gradeProfile90th: "AAB/C",
    prerequisites: [],
  },
  {
    programme: "Philosophy, Politics, and Economics",
    gradeProfile10th: "AAB/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Psychology",
    gradeProfile10th: "AAC/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Public Policy & Global Affairs",
    gradeProfile10th: "AAB/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Sociology",
    gradeProfile10th: "BBC/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Arts (Academic Discipline & Education)",
    gradeProfile10th: "BCC/B",
    gradeProfile90th: "AAA/A",
    prerequisites: [],
  },
  {
    programme: "Science (Academic Discipline & Education)",
    gradeProfile10th: "BBC/C",
    gradeProfile90th: "AAA/A",
    prerequisites: [
      {
        subject: "Mathematics",
        type: "recommended",
      },
    ],
  },
  {
    programme: "Sport Science & Management",
    gradeProfile10th: "BCC/D",
    gradeProfile90th: "AAB/B",
    prerequisites: [],
  },
] as const;

function toSlug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

function toCourseTargets({
  university,
  rows,
  source,
}: {
  university: UasCourseTarget["university"];
  rows: readonly {
    programme: string;
    gradeProfile10th: string;
    gradeProfile90th: string;
    prerequisites?: readonly SubjectPrerequisite[];
  }[];
  source: UasCourseTargetSource;
}): readonly UasCourseTarget[] {
  const assumedGp = source.assumedGpGrade;
  if (!assumedGp) return [];

  return rows
    .map((row) => {
      const baseUas10th = gradeProfileToBaseUas(
        row.gradeProfile10th,
        assumedGp
      );
      const baseUas90th = gradeProfileToBaseUas(
        row.gradeProfile90th,
        assumedGp
      );
      if (baseUas10th == null || baseUas90th == null) return null;

      const label = `${university} ${row.programme}`;
      return {
        id: `${university.toLowerCase()}-${toSlug(row.programme)}`,
        label,
        university,
        programme: row.programme,
        gradeProfile10th: row.gradeProfile10th,
        gradeProfile90th: row.gradeProfile90th,
        baseUas10th,
        baseUas90th,
        source,
        prerequisites: row.prerequisites ?? [],
      };
    })
    .filter((row): row is UasCourseTarget => row != null);
}

export const uasCourseTargets: readonly UasCourseTarget[] = [
  ...toCourseTargets({
    university: "NUS",
    rows: nusRows,
    source: nusIgpSource,
  }),
  ...toCourseTargets({
    university: "SMU",
    rows: smuRows,
    source: smuIgpSource,
  }),
  ...toCourseTargets({
    university: "NTU",
    rows: ntuRows,
    source: ntuIgpSource,
  }),
].sort((a, b) => a.label.localeCompare(b.label));

export function findUasCourseTargetByLabel(
  label: string
): UasCourseTarget | null {
  const normalised = label.trim().toLowerCase();
  if (normalised.length === 0) return null;

  return (
    uasCourseTargets.find(
      (row) => row.label.trim().toLowerCase() === normalised
    ) ?? null
  );
}
